import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSite } from '../context/SiteContext';
import { logoutParticipant } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (user) => {
  if (!user) return '?';
  const first = user.firstName || '';
  const last  = user.lastName  || '';
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first)         return first.slice(0, 2).toUpperCase();
  if (user.email)    return user.email[0].toUpperCase();
  return '?';
};

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('govirtual_user') || sessionStorage.getItem('govirtual_user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const isLoggedIn = () =>
  !!(localStorage.getItem('govirtual_token') || sessionStorage.getItem('govirtual_token'));

// ─── Profile Popout ───────────────────────────────────────────────────────────
const ProfilePopout = ({ user, primary, onLogout, onGoToLobby, hasLobby }) => {
  const initials = getInitials(user);
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null;

  return (
    <Box sx={{
      position: 'absolute', top: 'calc(100% + 10px)', right: 0,
      width: 260, bgcolor: '#fff', borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 1px 0 rgba(0,0,0,0.04)',
      border: '1px solid rgba(0,0,0,0.07)', overflow: 'hidden', zIndex: 10,
    }}>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
            {initials}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {fullName && <Typography variant="body2" fontWeight={700} noWrap>{fullName}</Typography>}
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{user.email}</Typography>
          </Box>
        </Box>
        {user.employeeNumber && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>Employee No.</Typography>
            <Typography variant="caption" fontWeight={600}>{user.employeeNumber}</Typography>
          </Box>
        )}
      </Box>

      {/* Go to Lobby — only shown when a lobby exists */}
      {hasLobby && (
        <Box onClick={onGoToLobby} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, cursor: 'pointer', color: primary, '&:hover': { bgcolor: `${primary}08` }, transition: 'background 0.15s', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>🏛</Box>
          <Typography variant="body2" fontWeight={600}>Go to Lobby</Typography>
        </Box>
      )}

      <Box onClick={onLogout} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, cursor: 'pointer', color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' }, transition: 'background 0.15s' }}>
        <LogoutIcon sx={{ fontSize: 17 }} />
        <Typography variant="body2" fontWeight={600}>Log Out</Typography>
      </Box>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN NAVBAR
// ═════════════════════════════════════════════════════════════════════════════
const EventNavbar = () => {
  const { event, forms, organization, lobby: ctxLobby, navMode } = useSite();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [scrolled,   setScrolled]   = useState(false);
  const [loggedIn,   setLoggedIn]   = useState(isLoggedIn);
  const [user,       setUser]       = useState(getStoredUser);
  const [popoutOpen, setPopoutOpen] = useState(false);
  const [lobby,      setLobby]      = useState(ctxLobby || null);
  const popoutRef = useRef(null);

  // In lobby/plenary/exhibitors modes, fetch the lobby if not in context.
  // New URL structure:
  //   prefixed: /:eventSlug/:lobbySlug  or  /:eventSlug/:lobbySlug/:pageSlug
  //   flat:     /:lobbySlug             or  /:lobbySlug/:pageSlug
  // So lobby slug is: segs[1] for prefixed, segs[0] for flat
  const RESERVED_SLUGS = new Set(['login', 'register', 'logout', 'verify', 'plenary', 'exhibitors', 'networking']);
  useEffect(() => {
    if ((navMode === 'lobby' || navMode === 'plenary') && !lobby && event?._id) {
      const segs = location.pathname.split('/').filter(Boolean);
      const isFlat = event?.urlMode === 'flat';
      const candidateSlug = isFlat ? segs[0] : segs[1];
      // Skip if the candidate is a reserved word — we're mid-redirect
      if (!candidateSlug || RESERVED_SLUGS.has(candidateSlug)) return;
      const url = `/lobbies/public/${event._id}/${candidateSlug}`;
      import('../services/api').then(({ default: api }) => {
        api.get(url).then(res => setLobby(res.data.data)).catch(() => {});
      });
    }
  }, [navMode, event, location.pathname]);

  // Sync ctxLobby if it arrives later
  useEffect(() => { if (ctxLobby) setLobby(ctxLobby); }, [ctxLobby]);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    const h = (e) => { if (popoutRef.current && !popoutRef.current.contains(e.target)) setPopoutOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = () => { setLoggedIn(isLoggedIn()); setUser(getStoredUser()); };
    window.addEventListener('storage', h);
    const id = setInterval(h, 1000);
    return () => { window.removeEventListener('storage', h); clearInterval(id); };
  }, []);

  const handleLogout = async () => {
    // Notify server to invalidate the session
    try { await logoutParticipant(); } catch {}
    ['govirtual_token','govirtual_user'].forEach(k => { localStorage.removeItem(k); sessionStorage.removeItem(k); });
    setLoggedIn(false); setUser(null); setPopoutOpen(false);
    navigate(event?.urlMode === 'flat' ? '/' : (event ? `/${event.slug}` : '/'));
  };

  const branding  = event?.branding || {};
  const primary   = branding.primaryColor   || '#1565C0';
  const secondary = branding.secondaryColor || '#FFA726';
  const logoUrl   = branding.logoUrl || organization?.logo || null;
  const logoLabel = event?.name || organization?.name || 'GoVirtual';
  const regForm      = (forms || []).find(f => f.formType === 'registration' && f.status === 'published');
  const loginEnabled = event?.login?.loginEnabled !== false; // default true

  // ── Nav links depending on mode ───────────────────────────────────────────
  const lobbyNavLinks = (() => {
    const pages = lobby?.pages?.filter(p => p.isActive) || [];
    if (pages.length) {
      return pages.map(p => ({ label: p.label, pageId: p.id, slug: p.slug }));
    }
    return [
      { label: 'Lobby',      pageId: 'lobby',      slug: null },
      { label: 'Plenary',    pageId: 'plenary',    slug: 'plenary' },
      { label: 'Exhibitors', pageId: 'exhibitors', slug: 'exhibitors' },
    ];
  })();

  const eventNavLinks = (() => {
    const defaults = [
      { label: 'Home',     type: 'scroll', target: 'home' },
      { label: 'Speakers', type: 'scroll', target: 'speakers' },
      { label: 'Schedule', type: 'scroll', target: 'schedule' },
    ];
    return event?.navLinks?.length > 0
      ? [...event.navLinks].sort((a, b) => a.order - b.order)
      : defaults;
  })();

  // Determine active page slug by checking the last URL segment against
  // known page slugs — works for both /:eventSlug/:lobbySlug/:pageSlug
  // and the shortcut /:eventSlug/:pageSlug.
  const urlSegs     = location.pathname.split('/').filter(Boolean);
  const lastSeg     = urlSegs[urlSegs.length - 1] || null;
  const knownSlugs  = lobbyNavLinks.map(l => l.slug).filter(Boolean);
  const lobbySlug   = lobby?.slug || null;
  // activePageSlug = last segment if it's a known page slug, else null (lobby root)
  const activePageSlug = knownSlugs.includes(lastSeg) ? lastSeg : null;

  const handleLobbyNavClick = (link) => {
    if (!link.slug) {
      // Lobby root
      const path = event?.urlMode === 'flat'
        ? `/${lobbySlug || 'lobby'}`
        : `/${event?.slug}/${lobbySlug || 'lobby'}`;
      navigate(path);
      return;
    }
    // Use direct shortcut URL: /:eventSlug/:pageSlug
    const path = event?.urlMode === 'flat'
      ? `/${link.slug}`
      : `/${event?.slug}/${link.slug}`;
    navigate(path);
  };

  const handleEventNavClick = (link) => {
    if (link.type === 'scroll') {
      const el = document.getElementById(link.target);
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
      if (event) {
        const path = event.urlMode === 'flat' ? '/' : `/${event.slug}`;
        navigate(path);
        setTimeout(() => document.getElementById(link.target)?.scrollIntoView({ behavior: 'smooth' }), 300);
      }
    } else {
      navigate(link.target);
    }
  };

  const handleLogoClick = () => {
    navigate(event?.urlMode === 'flat' ? '/' : (event ? `/${event.slug}` : '/'));
  };

  // Back button — label and destination depend on what page we're on.
  // We detect exhibitor booth pages by checking if the last URL segment
  // matches a known page slug (plenary/exhibitors) — if not, we're on a booth.
  const urlSegsForBack = location.pathname.split('/').filter(Boolean);
  const lastSegForBack = urlSegsForBack[urlSegsForBack.length - 1] || '';
  const isOnExhibitorBooth = navMode === 'plenary'
    && !knownSlugs.includes(lastSegForBack)
    && lastSegForBack !== (lobby?.slug);

  const backLabel = isOnExhibitorBooth ? 'Back to Exhibitions' : 'Back to Lobby';
  const backPath  = isOnExhibitorBooth
    // Go to exhibitors page
    ? (event?.urlMode === 'flat' ? '/exhibitors' : `/${event?.slug}/exhibitors`)
    // Go to lobby root
    : (event?.urlMode === 'flat' ? `/${lobbySlug || 'lobby'}` : `/${event?.slug}/${lobbySlug || 'lobby'}`);

  const handleBack = () => navigate(backPath);

  const handleGoToLobby = () => {
    const path = event?.urlMode === 'flat'
      ? `/${lobbySlug || 'lobby'}`
      : `/${event?.slug}/${lobbySlug || 'lobby'}`;
    navigate(path);
    setPopoutOpen(false);
  };

  const handleRegister = () => {
    if (!regForm) return;
    navigate(event?.urlMode === 'flat' ? `/${regForm.slug}` : `/${event.slug}/${regForm.slug}`);
  };

  const initials = getInitials(user);

  // ── All modes use the floating pill style ────────────────────────────────
  const isLobbyMode = navMode === 'lobby' || navMode === 'plenary';

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        top: { xs: 10, md: 16 },
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1200,
        width: { xs: 'calc(100% - 32px)', md: 'auto' },
        minWidth: { md: 680 },
        maxWidth: { md: 1200 },
        transition: 'all 0.3s ease',
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 56,
        px: { xs: 2, md: 3 },
        gap: 2,
        bgcolor: scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderRadius: 100,
        border: '1px solid rgba(255,255,255,0.6)',
        boxShadow: scrolled
          ? '0 8px 32px rgba(0,0,0,0.12), 0 1px 0 rgba(255,255,255,0.8) inset'
          : '0 4px 24px rgba(0,0,0,0.08), 0 1px 0 rgba(255,255,255,0.8) inset',
        transition: 'all 0.3s ease',
      }}>

        {/* ── Left: logo / back button ── */}
        {navMode === 'plenary' ? (
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            onClick={handleBack}
            sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 13, flexShrink: 0, px: 1 }}
          >
            {backLabel}
          </Button>
        ) : (
          // Event / lobby: normal logo
          <Box onClick={handleLogoClick} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, cursor: 'pointer' }}>
            {logoUrl ? (
              <Box component="img" src={logoUrl} alt={logoLabel} sx={{ height: 28, width: 'auto', objectFit: 'contain' }} />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <LocationOnIcon sx={{ color: '#fff', fontSize: 15 }} />
                </Box>
                <Typography fontWeight={700} sx={{ color: primary, letterSpacing: -0.5, fontSize: 15, whiteSpace: 'nowrap' }}>
                  go<span style={{ color: secondary }}>virtual</span>
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── Centre: nav links ── */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', flex: 1, justifyContent: 'center', gap: 0 }}>
          {(isLobbyMode ? lobbyNavLinks : eventNavLinks).map((link) => {
            const isActive = isLobbyMode
              ? link.slug === activePageSlug  // null===null for Lobby, 'plenary'==='plenary' for Plenary, etc.
              : false;

            return (
              <Button
                key={link.label}
                onClick={() => isLobbyMode ? handleLobbyNavClick(link) : handleEventNavClick(link)}
                sx={{
                  fontSize: 14, px: 1.75, py: 0.75, minWidth: 'auto',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? primary : 'text.primary',
                  borderRadius: 100,
                  bgcolor: isActive && isLobbyMode ? `${primary}12` : 'transparent',
                  '&:hover': { bgcolor: `${primary}10` },
                }}
              >
                {link.label}
              </Button>
            );
          })}
        </Box>

        {/* ── Right: action area (same in all modes) ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {loggedIn && user ? (
            <Box ref={popoutRef} sx={{ position: 'relative' }}>
              <Box
                onClick={() => setPopoutOpen(o => !o)}
                title={[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                sx={{
                  width: 36, height: 36, borderRadius: '50%',
                  bgcolor: primary, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  border: popoutOpen ? `2px solid ${primary}` : '2px solid transparent',
                  outline: popoutOpen ? `2px solid ${primary}30` : 'none',
                  transition: 'all 0.2s',
                  '&:hover': { filter: 'brightness(0.9)' },
                  userSelect: 'none',
                }}
              >
                {initials}
              </Box>
              {popoutOpen && (
                <ProfilePopout
                  user={user}
                  primary={primary}
                  onLogout={handleLogout}
                  onGoToLobby={handleGoToLobby}
                  hasLobby={!!lobbySlug}
                />
              )}
            </Box>
          ) : (
            <>
              {regForm && loginEnabled && (
                <Button
                  onClick={() => navigate(event?.urlMode === 'flat' ? '/login' : `/${event?.slug}/login`)}
                  sx={{ color: primary, fontWeight: 600, fontSize: 14, px: 1.5, display: { xs: 'none', md: 'inline-flex' } }}
                >
                  Login
                </Button>
              )}
              {regForm && (
                <Button variant="contained" onClick={handleRegister}
                  sx={{ bgcolor: primary, borderRadius: 100, px: { xs: 2, md: 2.5 }, py: 0.875, fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', boxShadow: `0 4px 12px ${primary}40`, '&:hover': { bgcolor: primary, filter: 'brightness(0.9)' } }}>
                  {event?.ctaLabel || 'Register Now'}
                </Button>
              )}
            </>
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default EventNavbar;
